<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">編輯 SEO</h1>
      <router-link class="btn btn-secondary" :to="{ name: 'WebsiteSeo' }">返回列表</router-link>
    </div>

    <div v-if="loading" class="loading-state"><div class="spinner" /><p>載入中...</p></div>
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="btn btn-secondary" @click="load">重試</button>
    </div>
    <form v-else class="form-card" @submit.prevent="save">
      <div class="meta-row">
        <div>
          <span class="meta-label">Path</span>
          <code>{{ item.path }}</code>
        </div>
        <div>
          <span class="meta-label">Page Key</span>
          <code>{{ item.pageKey }}</code>
        </div>
      </div>

      <div class="form-group">
        <label>Title</label>
        <input v-model="form.title" class="form-control" />
      </div>
      <div class="form-group">
        <label>Description</label>
        <textarea v-model="form.description" class="form-control" rows="3" />
      </div>
      <div class="form-group">
        <label>OG Title</label>
        <input v-model="form.ogTitle" class="form-control" />
      </div>
      <div class="form-group">
        <label>OG Description</label>
        <textarea v-model="form.ogDescription" class="form-control" rows="2" />
      </div>
      <div class="form-group">
        <label>OG Image（社群分享圖）</label>
        <ImageUploader
          v-model="form.ogImage"
          :upload-fn="uploadImage"
          shape="card"
          aspect-ratio="1200 / 630"
          :crop-width="1200"
          :crop-height="630"
          accept="image/jpeg,image/png,image/webp"
          :max-size-mb="5"
          hint="輸出 1200 × 630 px（約 1.91:1），可拖曳／縮放裁切"
          size-hint="支援 JPEG／PNG／WebP，單檔上限 5MB；預覽與 Facebook／LINE 分享比例一致"
          placeholder="上傳 OG Image"
          upload-label="上傳 OG 圖"
          replace-label="更換 OG 圖"
        />
      </div>
      <div class="form-group">
        <label>Canonical URL</label>
        <input
          v-model="form.canonicalUrl"
          class="form-control"
          placeholder="例如 https://www.lockline.io/contact"
        />
        <p class="field-hint">
          告訴搜尋引擎「這頁的正式網址」。若同一內容有多個網址（參數、舊路徑、鏡像站），填此欄可避免被當成重複內容。未填時會依頁面 path 預設為前台正式網址（例如 contact → https://www.lockline.io/contact）。
        </p>
      </div>
      <div class="form-group">
        <label>Robots</label>
        <input v-model="form.robots" class="form-control" placeholder="index,follow" />
      </div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          {{ saving ? '儲存中…' : '儲存' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import ImageUploader from '@/components/ImageUploader.vue'
import { websiteSeoService } from '@/api/modules/website.js'
import { showSuccess, showError } from '@/utils/swal.js'

const WWW_SITE_URL = (process.env.VUE_APP_WWW_URL || 'https://www.lockline.io').replace(/\/$/, '')

function defaultCanonicalUrl(path, pageKey) {
  const resolvedPath = path
    || (pageKey === 'home' ? '/' : (pageKey ? `/${pageKey}` : ''))
  if (!resolvedPath || resolvedPath === '/') return WWW_SITE_URL
  return `${WWW_SITE_URL}${resolvedPath.startsWith('/') ? resolvedPath : `/${resolvedPath}`}`
}

export default {
  name: 'WebsiteSeoForm',
  components: { ImageUploader },
  data() {
    return {
      loading: false,
      saving: false,
      error: '',
      item: {
        pageKey: '',
        path: '',
      },
      form: {
        title: '',
        description: '',
        ogTitle: '',
        ogDescription: '',
        ogImage: '',
        canonicalUrl: '',
        robots: 'index,follow',
      },
    }
  },
  computed: {
    pageKey() {
      return this.$route.params.pageKey
    },
  },
  watch: {
    pageKey: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    uploadImage(file) {
      return websiteSeoService.uploadImage(file)
    },
    async load() {
      if (!this.pageKey) return
      this.loading = true
      this.error = ''
      try {
        const res = await websiteSeoService.get(this.pageKey)
        // apiClient 已解包為 { success, data }，SEO 物件在 res.data
        const item = res?.data || {}
        const path = item.path || (this.pageKey === 'home' ? '/' : `/${this.pageKey}`)
        this.item = {
          pageKey: item.pageKey || this.pageKey,
          path,
        }
        this.form = {
          title: item.title || '',
          description: item.description || '',
          ogTitle: item.ogTitle || '',
          ogDescription: item.ogDescription || '',
          ogImage: item.ogImage || '',
          // DB 有值用 DB；否則依 path／pageKey 預填（blog → …/blog）
          canonicalUrl: item.canonicalUrl || defaultCanonicalUrl(path, this.pageKey),
          robots: item.robots || 'index,follow',
        }
      } catch (e) {
        this.error = e?.response?.data?.message || '載入失敗'
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      try {
        const res = await websiteSeoService.update(this.pageKey, { ...this.form })
        const warning = res?.data?.cacheWarning
        showSuccess(warning ? `已儲存（快取警告：${warning}）` : '已儲存')
        this.$router.push({ name: 'WebsiteSeo' })
      } catch (e) {
        showError(e?.response?.data?.message || '儲存失敗')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.form-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  max-width: 720px;
}
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e2e8f0;
}
.meta-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 2px;
}
.form-group { margin-bottom: 14px; }
.form-group label { display: block; font-weight: 600; font-size: 13px; margin-bottom: 4px; }
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.form-actions { margin-top: 16px; }
</style>
